/* eslint-disable no-nested-ternary */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { flexbox, space } from 'styled-system';
import { useSelector, useDispatch } from 'react-redux';
import { getTaxDocumentsRequest, downloadTaxDocumentRequest } from 'redux-api/action/taxDocuments';
import taxDocumentsSelector from 'redux-api/reselect/taxDocuments';
import selectProfile from 'redux-api/reselect/profile';
import Text from 'theme/text';
import Table from 'theme/table/default';
import IconPdf from 'theme/icons/filetype-pdf';
import BtnLink from 'theme/buttons/btn-link';
import Plus from 'theme/icons/plus';
import translations from 'translations';

const Wrapper = styled.div`
  ${space}

  display: flex;
  align-items: center;
  flex-direction: column;

  & > div {
    width: 100%;
    max-width: 2100px;
  }
`;

const WrapTable = styled.div`
  background-color: ${themeGet('colors.white')};
  border: 2px solid ${themeGet('colors.primary_100')};
  border-radius: 12px;
  overflow: hidden;
`;

const WrapPdfBtn = styled.div`
  width: 100%;
  display: flex;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${flexbox}
`;

function DocumentsBilling() {
  const dispatch = useDispatch();

  const { push } = useRouter();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { languageCode } = useSelector((state) => state.config);
  const { isWaiting } = useSelector((state) => state.taxDocuments);
  const payload = useSelector(taxDocumentsSelector);
  const {
    payload: { company },
  } = useSelector(selectProfile);

  useEffect(() => {
    dispatch(getTaxDocumentsRequest({ page, pageSize }));
  }, [dispatch, page, pageSize]);

  const handleChangeSize = useCallback((current, s) => {
    setPageSize(s);
  }, []);

  const handleChange = useCallback(({ pagination: { current, pageSize: ps } }) => {
    if (current && ps) {
      setPage((current - 1) * ps);
    }
  }, []);

  const handleDownloadPdf = useCallback(
    (pid, path) => {
      dispatch(downloadTaxDocumentRequest({ pid, path }));
    },
    [dispatch],
  );

  const columns = useMemo(
    () => [
      {
        title: translations[languageCode].Name,
        dataIndex: 'name',
        key: 'name',
        width: 200,
        render: (name, row) => (
          <>
            <Text>{name}</Text>
            {row?.original?.description && <Text fontSize='small' fontWeight="bold" color="grey_400">{row?.original?.description}</Text>}
          </>
        ),
      },
      {
        title: translations[languageCode].Year,
        dataIndex: 'year',
        key: 'year',
        width: 50,
      },
      {
        title: translations[languageCode].Download,
        dataIndex: 'downloadLink',
        key: 'downloadLink',
        width: 50,
        render: (link, row) => (
          <WrapPdfBtn>
            <button type="button" onClick={() => handleDownloadPdf(row.id, link)}>
              <IconPdf />
            </button>
          </WrapPdfBtn>
        ),
      },
    ],
    [languageCode, handleDownloadPdf],
  );

  const handleOnClickNew = useCallback(() => push('/billing/tax-interview'), [push]);

  return (
    <Wrapper px="l" py="xs" my="xs">
      <div>
        <Row justifyContent="space-between">
          <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600" mt="m" mb="s">
            {translations[languageCode]['Your documents']}
          </Text>

          {company && company.code === 'SL_US' && (
            <BtnLink
              label={translations[languageCode]['Create new']}
              icon={<Plus />}
              onClick={handleOnClickNew}
            />
          )}
        </Row>

        <WrapTable>
          <Table
            emptyLabel={translations[languageCode]['no available']}
            columns={columns}
            dataSource={payload.data}
            pagination={{
              showSizeChanger: true,
              position: 'bottomCenter',
              pageSize,
              total: payload.totalCount,
              onShowSizeChange: handleChangeSize,
            }}
            total={payload.totalCount || 0}
            onChangeTable={handleChange}
            loading={isWaiting}
            languageCode={languageCode}
          />
        </WrapTable>
      </div>
    </Wrapper>
  );
}

export default DocumentsBilling;
